<template>
  <div class="position-relative d-flex overflow-hidden" @dragenter.prevent="handleFileDragEnter">
    <TopProgressBar ref="topProgress" />
    <div
      v-if="isDraggingFile"
      class="file-drop-overlay"
      @dragover.prevent
      @dragleave.prevent="handleFileDragLeave"
      @drop.prevent="handleFileDrop"
    >
      <div class="file-drop-overlay-content">
        <span class="material-icons">upload_file</span>
        <h4>Drop file to import</h4>
        <p class="mb-0">GPX, KML, KMZ, TCX or GeoJSON</p>
      </div>
    </div>
    <div v-if="isMobile" class="mobileCTA d-flex justify-content-center align-items-center p-3">
      <div class="text-center d-flex flex-column align-items-center justify-content-center" id="app-cta-overlay">
        <img class="rounded mb-3" alt="Hiiker app" width="250" src="../../images/app-hero-demo.webp">
        <h3><strong>Planning a hike on your mobile device?</strong></h3>
        <p><small>Get the best experience on the the HiiKER App</small></p>
        <a class="btn btn-primary mt-2" href="/app">
          <span>Open in app</span>
        </a>
      </div>
    </div>
    <div class="map-container">
      <div class="map-content">
        <PlanTrailRoutes
          @import="handleRouteImport"
          @add-new="handleAddNewRoute"
          @edit="handleEditRoute"
          @delete="handleDeleteRoute"
          @collapse="handleCollapseRoutes"
          @route-style-change="handleRouteStyleChange"
          @replace="handleReplaceRoute"
        />
        <PlanTrailActions
          @save="handleSaveAction"
          @share="handleShareAction"
          @collapse="handleCollapseActions"
        />
        <PlanTrailElevation />
        <div id='map' ref="mapElementRef" class="mapbox m-0 p-0" style="height: 90vh"></div>
        <MapButtons :button-clicked="buttonClicked" :buttons="buttons"/>

        <div class="bottom-controls-container">
          <div v-if="miniCoordsBar" class="coordsbar">
            <span class="material-icons">my_location</span> {{ miniCoordsBar }}
          </div>
          <ScaleBar v-if="mapInstance" :map="mapInstance" :latitude="currentLatitude" />
        </div>

        <MapControls :fit-bounds="activeRouteBounds"/>
        <TrailDataCard />
        <Loader v-if="miniLoader" />
        <MapContextMenu @add-custom-poi="handleAddCustomPOI" />
      </div>
    </div>

    <BaseMapStylesDialog/>
    <PlanTrailSaveModal/>
    <PlanTrailShareModal/>
    <GoToLocationDialog/>
    <TrailImportDialog/>
    <ReferenceEditWarningModal ref="referenceWarningModal" />
    <RouteLengthWarningModal ref="routeLengthWarningModal" />
    <SpotlightTour
      v-if="showMultiDayTutorial"
      :steps="multiDayTutorialSteps"
      @done="onMultiDayTutorialDone"
    />
    <LoadingDialog :show-loading-dialog="isLoading"/>
    <WaypointAddingNotification :enabled="waypointAddingModeEnabled" />
    <MapDataPOIDetailDialog/>
    <WaypointDialog/>
    <POIDialog 
      v-if="poiCoordinates"
      :coordinates="poiCoordinates"
      :existing-highlight="editingHighlight"
    />
    <HighlightDetailDialog />
    <Options :options="optionItems"/>

    <div class="my-silly-class"></div>
    <input id="file-selector" type="file" accept=".gpx" style="display: none" />
  </div>
</template>

<script lang="ts">
// Vue 3 imports
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, watch, getCurrentInstance } from 'vue';
import { useStore } from 'vuex'; // Import useStore

// Mapbox imports
import mapboxgl, { MapboxEvent, MapMouseEvent, Style } from "mapbox-gl"; // Keep types
import { Modal } from 'bootstrap';
import { nextTick } from 'vue';

// Existing component & utility imports (keep as they are)
import BaseMapStylesDialog from '../../apps/./base_map_dialog/BaseMapStylesDialog.vue';
import PlanTrailSaveModal from "./components/PlanTrailSaveModal.vue";
import PlanTrailShareModal from "./components/PlanTrailShareModal.vue";
import MapButtons from "../components/MapButtons.vue";
import GoToLocationDialog from "../../apps/go_to_location_dialog/GoToLocationDialog.vue";
import TrailImportDialog from "../../apps/trail_import_dialog/TrailImportDialog.vue";
import MapControls from "../components/MapControls.vue";
import LoadingDialog from "../../utils/shared_components/LoadingDialog.vue";
import WaypointAddingNotification from "../../apps/waypoint_adding_mode_notificaiton/WaypointAddingNotification.vue";
import WaypointDialog from "../../apps/waypoint_dialog/WaypointDialog.vue";
import Loader from "../../utils/shared_components/Loader.vue";
import TrailDataCard from "../../apps/trail_data_card/TrailDataCard.vue";
import { OnBoardingGoal } from "../../utils/models/OnBoardingGoal";
import { ThreeDimensionalMapManager } from "../../utils/models/MapStyles";
import { RevenueCatWrapper } from "../../utils/RevenueCatWrapper";
import createGeoLocate from "../../utils/helpers/GeoLocateHelper";
import { isFileDrag, getSupportedImportFile } from "../../utils/helpers/DragDropImportHelper";
import { SavedCenter, HiikerMapStyle } from "../../utils/models/MapStyles";
import TopProgressBar from '../../utils/shared_components/TopProgressBar.vue';
import emitter from "../../utils/bus";
import { Tooltip } from 'bootstrap'
import {LayerRegistry} from "../../utils/layer-registry";
import MapDataPOIDetailDialog from '../map_data_poi_dialog/MapDataPOIDetailDialog.vue';
import PlanTrailRoutes from "./components/PlanTrailRoutes.vue";
import PlanTrailActions from "./components/PlanTrailActions.vue";
import PlanTrailElevation from "./components/PlanTrailElevation.vue";
import MapContextMenu from "./components/MapContextMenu.vue";
import { isPlannerMobile, nextPlannerGateOnResize } from "./plannerMobileGate";
import POIDialog from "./components/POIDialog.vue";
import HighlightDetailDialog from "./components/HighlightDetailDialog.vue";
import Options from "../../apps/options/Options.vue";
import { OptionItem, OptionType, optionConfig } from "../../utils/models/OptionItem";
import ScaleBar from "../components/ScaleBar.vue";
import ReferenceEditWarningModal from "./components/ReferenceEditWarningModal.vue";
import RouteLengthWarningModal from "./components/RouteLengthWarningModal.vue";
import { isRouteLengthWarningDismissed } from "./services/routeLengthWarningPreference";
import SpotlightTour from "./components/SpotlightTour.vue";
import { hasSeenMultiDayTutorial, markMultiDayTutorialSeen } from "./utils/multiDayTutorialSeen";
// A dynamically-bound `:src` (unlike a static template `src="..."`) isn't
// rewritten by Vite at build time, so the gif must be imported as a module
// to get its resolved asset URL rather than passed as a raw relative path.
import multiDayDraggerGif from "../../images/multi-day-dragger.gif";

// Assume jQuery '$' is available globally if needed for tooltips/modals
declare var $: any;
// Keep window augmentations if necessary for global access patterns
declare global {
  interface Window {
    map: mapboxgl.Map; // Already declared, ensure consistency
    geoLocate: mapboxgl.GeolocateControl; // Already declared
    user: any; // Assuming window.user exists globally
    onBoardingVue: any; // Assuming window.onBoardingVue exists globally
    bus: any; // Assuming window.bus (event bus) exists globally
    showHighlightDetail: (highlight: any) => void; // For showing highlight details
    openHighlightEditDialog: (highlight: any) => void; // For editing highlights via modal
    checkReferenceEditWarning: (route?: any) => Promise<boolean>; // Check before editing referenced routes (async with modal); defaults to the active route
    canEditReference: (route?: any) => boolean; // Synchronously check if editing is allowed (no modal); defaults to the active route
  }
}


export default defineComponent({
  name: 'PlanTrail', // Optional: Add component name
  components: {
    // List components used in the template
    TrailDataCard, Loader,
    WaypointDialog, WaypointAddingNotification, LoadingDialog,
    MapButtons, BaseMapStylesDialog, PlanTrailSaveModal,
    PlanTrailShareModal, GoToLocationDialog, TrailImportDialog, MapControls, TopProgressBar, MapDataPOIDetailDialog,
    PlanTrailRoutes, PlanTrailActions, PlanTrailElevation, MapContextMenu, POIDialog, HighlightDetailDialog, Options, ScaleBar,
    ReferenceEditWarningModal,
    RouteLengthWarningModal,
    SpotlightTour
  },
  setup() {
    // Get the Vuex store instance
    const store = useStore();

    // --- Refs for component state (previously data) ---
    const optionItems = ref<OptionItem[]>([]);
    const buttons = ref([
      { id: 1, name: 'Maps', icon: 'layers', enabled: false, dropIcon: true },
      { id: 2, name: 'Options', icon: 'add', enabled: false, dropIcon: true },
    ]);

    // --- Refs for template elements ---
    const topProgress = ref<any>(null); // Ref for vue-topprogress
    const mapElementRef = ref<HTMLElement | null>(null); // Ref for the map div if needed, though we use ID
    const referenceWarningModal = ref<any>(null); // Ref for the reference edit warning modal
    const routeLengthWarningModal = ref<any>(null); // Ref for the route length warning modal

    // --- Computed properties (previously computed) ---
    const title = computed({
      get: () => store.state.planTrail.title,
      set: (value) => store.commit('planTrail/setTitle', value)
    });

    const routePreference = computed({
      get: () => store.state.planTrail.routePreference,
      set: (value) => store.commit('planTrail/setRoutePreference', value)
    });

    // Map state directly
    const waypointAddingModeEnabled = computed(() => store.state.waypoints.waypointAddingModeEnabled);
    const selectedPOI = computed(() => store.state.poi.selectedPOI); // Keep if used indirectly
    const miniLoader = computed(() => store.state.mapDataOverlays?.miniLoader);
    const isSnap = computed(() => store.state.planTrail.isSnap);
    const removalMode = computed(() => store.state.planTrail.removalMode);
    const isLoading = computed(() => store.state.planTrail.loading);
    const miniCoordsBar = computed(() => store.state.planTrail.miniCoordsBar);


    // Map getters directly
    const trailDistanceString = computed(() => store.getters['planTrail/trailDistanceString']);
    const elevationGainString = computed(() => store.getters['planTrail/elevationGainString']);
    const activeRouteBounds = computed(() => store.getters['planTrail/activeRouteBounds']);

    // --- Watchers ---
    // Only refresh when the active route selection changes, not on every route mutation
    watch(() => store.state.planTrail.activeRouteKey, () => {
      store.dispatch('planTrail/addAllRoutesToMap');
    });

    // Show a one-time warning when total distance exceeds 30km
    watch(
      () => store.getters['planTrail/totalDistance'],
      (newDistance: number) => {
        const DISTANCE_THRESHOLD = 30000; // 30km in meters
        const routeCount = Object.keys(store.state.planTrail.routes).length;
        if (
          newDistance > DISTANCE_THRESHOLD &&
          !store.state.planTrail.routeLengthWarningShown &&
          !isRouteLengthWarningDismissed() &&
          routeCount <= 1
        ) {
          store.state.planTrail.routeLengthWarningShown = true;
          if (routeLengthWarningModal.value) {
            routeLengthWarningModal.value.show();
          }
        }
      }
    );

    // --- Multi-day spotlight tutorial ---
    // Launches once (localStorage-backed, see utils/multiDayTutorialSeen.ts)
    // the moment a plan is (or becomes) multi-day — covers both landing on
    // an existing multi-day hike (the immediate call below fires once
    // maxDay is populated by initialiseStore) and adding a route/day while
    // planning that crosses from 1 day to 2+.
    const showMultiDayTutorial = ref(false);
    const multiDayTutorialUserKey = computed(() => String(window.user?.id ?? 'anon'));

    const multiDayTutorialSteps = [
      {
        title: 'Planning multiple days',
        description: "Edit the start and end points of each day to set your rest and refill stops.",
        image: multiDayDraggerGif,
      },
      {
        // Highlight the whole unlocked toolbar, not just the bare "Lock"
        // text-link — that button has no visual chrome of its own (no
        // border/background, just text), so spotlighting only its tiny rect
        // reads as far less prominent than the Pace Wizard step's chip
        // highlight. The whole toolbar box gives a comparably substantial cutout.
        selector: '.guided-toolbar--unlocked',
        title: 'Lock the route',
        description: "Locking freezes your route to the trail, so every day always follows it exactly — you can then plan overnight stops without altering the route itself.",
        example: {
          before: { icon: 'lock_open', label: 'Free planning', variant: 'muted' },
          after: { icon: 'lock', label: 'Locked', variant: 'active' },
        },
      },
      {
        selector: '.guided-toolbar__wizard-chip',
        title: 'Pace wizard',
        description: 'Once locked, use the Pace Wizard to re-split your days by a realistic daily pace.',
        fallbackNote: 'Once you lock the route, a Pace Wizard button appears here to help plan realistic daily distances.',
      },
      {
        selector: '.day-card__header',
        title: 'Expand a day',
        description: 'Click any day to expand it and see its routes, distance and elevation.',
      },
    ];

    function maybeShowMultiDayTutorial() {
      if (showMultiDayTutorial.value) return;
      if (hasSeenMultiDayTutorial(multiDayTutorialUserKey.value)) return;
      showMultiDayTutorial.value = true;
    }

    function onMultiDayTutorialDone() {
      markMultiDayTutorialSeen(multiDayTutorialUserKey.value);
      showMultiDayTutorial.value = false;
    }

    watch(
      () => store.getters['planTrail/maxDay'],
      (newMaxDay: number, oldMaxDay: number | undefined) => {
        if (newMaxDay >= 2 && (oldMaxDay === undefined || oldMaxDay < 2)) {
          maybeShowMultiDayTutorial();
        }
      },
      { immediate: true }
    );

    // --- Map Instance ---
    // Keep map instance accessible within setup scope, but not reactive unless needed
    let map: mapboxgl.Map | null = null;

    // Reactive map instance and current latitude for scale bar
    const mapInstance = ref<mapboxgl.Map | null>(null);
    const currentLatitude = ref<number>(0);

    // --- Methods (converted to functions) ---
    const mapMoved = (event: MapboxEvent) => {
      SavedCenter.saveCurrentCenter(); // Assumes SavedCenter is globally accessible or imported correctly
      // Update current latitude for scale bar calculations
      if (map) {
        currentLatitude.value = map.getCenter().lat;
      }
    };

    const mapClicked = async (e: MapMouseEvent) => {
      // Use store variable directly
      await store.dispatch('planTrail/mapClicked', e);
    };

    const mapRightClicked = (e: MapMouseEvent) => {
      // Prevent default browser context menu
      e.preventDefault();

      // Query features at the clicked point
      const features = window.map.queryRenderedFeatures(e.point);

      // Get the map container's position on the page
      const mapContainer = window.map.getContainer();
      const rect = mapContainer.getBoundingClientRect();

      // Convert canvas-relative coordinates to page coordinates
      const pageX = rect.left + e.point.x;
      const pageY = rect.top + e.point.y;

      // Dispatch a custom event for the context menu component
      const event = new CustomEvent('map-context-menu', {
        detail: {
          x: pageX,
          y: pageY,
          lngLat: e.lngLat,
          features: features
        }
      });
      window.dispatchEvent(event);
    };

    const mapStyleLoaded = () => {
      // Use store variable directly
      // AddAllMarkersToMap and addAllLineStringsToMap likely depend on the map style
      // being fully loaded and sources/layers potentially needing re-adding.
      // This timeout might be brittle; consider using map's 'idle' event or
      // ensuring these dispatches correctly handle style changes internally.
      setTimeout(async () => {
        await store.dispatch('planTrail/addAllRoutesToMap');
        // Re-add highlights (they are on separate layers destroyed by style change)
        await store.dispatch('planTrail/addHighlightsToMap');
        // Re-add tracer route if one exists (it gets removed on style change)
        await store.dispatch('planTrail/reAddTracerRoute');
        // Setup POI interactions after map style loads
        await store.dispatch('planTrail/setupPOIInteractions');
      }, 1000); // Keep timeout for minimal change
    };

    // Mouse move is now handled by layer-specific events in setupOptimizedHover

    const handleMergeDayNames = (dayNamesMap: Record<number, { name: string; description: string }>) => {
      const existing = { ...(store.state.planTrail.dayInfo || {}) };
      Object.entries(dayNamesMap).forEach(([day, info]) => {
        existing[Number(day)] = { ...(existing[Number(day)] || {}), ...info };
      });
      store.commit('planTrail/setDayInfo', existing);
    };

    const handleRouteStyleChanged = () => store.dispatch('planTrail/addAllRoutesToMap');

    const handleGeoJsonUpdate = (data: any) => {
      // Handle both old format (geoJson, trailName) and new format (object)
      if (data.geoJson) {
        // New format - data is already an object with all properties
        store.dispatch('planTrail/importFromGeoJson', data);
      } else {
        // Old format - data is the geoJson itself, trailName is second arg
        // This handles existing calls from other parts of the app
        store.dispatch('planTrail/importFromGeoJson', {
          geoJson: data,
          trailName: arguments[1]
        });
      }
    };


    const saveClicked = (event: Event) => {
      event.preventDefault();
      // Access global window variables directly
      if (window.user == null) {
        window.onBoardingVue?.launchFor(OnBoardingGoal.CreateAccount); // Use optional chaining
        return;
      }
      // Use jQuery if still needed for modals
      const element = document.getElementById("save-plan-dialog");
      const modal = new Modal(element)
      modal.show();
    };

    const shareClicked = (event: Event) => {
      event.preventDefault();
      if (window.user == null) {
        window.onBoardingVue?.launchFor(OnBoardingGoal.CreateAccount); // Use optional chaining
        return;
      }
      store.dispatch('planTrail/shareRoute');
    };

    const buttonClicked = async (id: number) => {
      const isProOrProPlus = await RevenueCatWrapper.isProOrProPlus();
      switch (id) {
        case 0: // Search
          const element = document.getElementById("go-to-location-dialog");
          const modal = new Modal(element)
          modal.show();
          break;
        case 1: // Change Map
          if (window.user) {
            const element = document.getElementById("map-styles-dialog");
            const modal = new Modal(element)
            modal.show();
          } else {
            window.onBoardingVue?.launchFor(OnBoardingGoal.MapStyles);
          }
          break;
        case 2: // Options
          const optionsElement = document.getElementById("options-dialog");
          const optionsModal = new Modal(optionsElement);
          optionsModal.show();
          break;
        case 3: // Waypoints
          if (window.user) {
            const element = document.getElementById("waypoint-options-dialog");
            const modal = new Modal(element)
            modal.show();
          } else {
            window.onBoardingVue?.launchFor(OnBoardingGoal.Waypoints);
          }
          break;
        case 4: // Print Map
          window.location.href = "/print-maps";
          break;
      }
    };

    const undoClicked = async () => {
      await store.dispatch('planTrail/undo');
    };

    const redoClicked = async () => {
      await store.dispatch('planTrail/redo');
    };

    const snapClicked = () => {
      store.commit('planTrail/toggleSnap');
    };


    const outAndBackClicked = () => { // Keep if used elsewhere
      store.dispatch('planTrail/outAndBackClicked');
    };

    const poiCoordinates = ref<[number, number] | null>(null);
    const editingHighlight = ref<any | null>(null);

    const cleanupOrphanedBackdrops = () => {
      const openModals = document.querySelectorAll('.modal.show');
      const backdrops = document.querySelectorAll('.modal-backdrop');

      backdrops.forEach((backdrop, index) => {
        if (index >= openModals.length) {
          backdrop.remove();
        }
      });

      if (openModals.length === 0 && backdrops.length > 0) {
        document.body.classList.remove('modal-open');
        document.body.style.removeProperty('padding-right');
      }
    };

    const resetPOIModalState = () => {
      editingHighlight.value = null;
      poiCoordinates.value = null;
    };

    const showPOIDialog = ({ coordinates, highlight }: { coordinates?: [number, number]; highlight?: any }) => {
      editingHighlight.value = highlight ? JSON.parse(JSON.stringify(highlight)) : null;

      const resolvedCoordinates = coordinates
        ?? (highlight?.coordinates
          ? [highlight.coordinates[0], highlight.coordinates[1]] as [number, number]
          : null);

      if (!resolvedCoordinates) {
        console.warn('Unable to determine coordinates for POI dialog');
        return;
      }

      // Spread into a new array so Vue picks up the change even if values match
      poiCoordinates.value = [resolvedCoordinates[0], resolvedCoordinates[1]] as [number, number];

      cleanupOrphanedBackdrops();

      nextTick(() => {
        const modalElement = document.getElementById('poi-dialog');
        if (!modalElement) {
          console.warn('POI dialog element not found');
          return;
        }

        const existingModal = Modal.getInstance(modalElement);
        const modal = existingModal ?? new Modal(modalElement);

        const handleHidden = () => {
          const instance = Modal.getInstance(modalElement);
          instance?.dispose();
          modalElement.removeEventListener('hidden.bs.modal', handleHidden);
          resetPOIModalState();
          cleanupOrphanedBackdrops();
        };

        modalElement.addEventListener('hidden.bs.modal', handleHidden);
        modal.show();
      });
    };

    const openHighlightEditDialog = (highlight: any) => {
      showPOIDialog({ highlight });
    };

    const handleAddCustomPOI = (event) => {
      const { lng, lat } = event.coordinates;
      showPOIDialog({ coordinates: [lng, lat] as [number, number] });
    };

    const save = async () => { // Keep if used by save modal
      // Access template ref's value
      this.$refs.topProgress.start();
      if (window.user) {
        // Access store state directly
        const activeRoute = store.getters['planTrail/activeRoute'];
        if (!activeRoute || activeRoute.markers.length < 2) {
          alert("You must have at least 2 markers to save a trail");
          this.$refs.topProgress.finish(); // Ensure progress stops
          return;
        }
        // Assume 'plannedTrailName' is an input within the modal
        let text = (document.getElementById('plannedTrailName') as HTMLInputElement)?.value ?? '';
        await store.dispatch('planTrail/save', text);
        setTimeout(() => {
          this.$refs.topProgress.finish();
        }, 900); // Keep timeout
      } else {
        $('#signup-dialog').modal('show'); // Ensure this modal ID exists
        this.$refs.topProgress.finish(); // Ensure progress stops if user not logged in
      }
    };

    const closeToastClicked = () => { // Keep if used by a toast component
      store.commit('planTrail/clearError');
    };

    const loopRoute = () => {
      store.dispatch('planTrail/loopRoute');
    };

    const reverseRoute = () => {
      store.dispatch('planTrail/reverseFullRoute');
    };

    const backtrackRoute = () => {
      store.dispatch('planTrail/backtrackRoute');
    };

    const addTracerRoute = () => {
      const input = document.getElementById('file-selector') as HTMLInputElement | null;
      if (!input) {
        return;
      }

      input.type = 'file';
      input.accept = '.gpx';
      input.value = '';

      input.onchange = async (e: Event) => {
        const target = e.target as HTMLInputElement | null;
        if (!target || !target.files || target.files.length === 0) {
          return;
        }
        const file = target.files[0];
        try {
          await store.dispatch('planTrail/addTracerRoute', file);
        } finally {
          target.value = '';
        }
      };

      input.click();
    };

    const updateElevation = () => {
      store.dispatch('planTrail/updateElevation');
    };

    const clearMap = () => {
      store.dispatch('planTrail/clearMap');
    };


    const _3dClicked = async () => { // Keep if used (e.g. by MapControls component)
      const isProOrProPlus = await RevenueCatWrapper.isProOrProPlus();
      if (isProOrProPlus) {
        store.dispatch('mapStyles/toggle3dMaps'); // Ensure this action exists
      } else {
        window.onBoardingVue?.launchFor(OnBoardingGoal.MapStyles);
      }
    };

    // --- Keyboard Shortcuts Handler ---
    const handleKeyboardShortcuts = (event: KeyboardEvent) => {
      // Check if user is typing in an input field
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const isCtrlOrCmd = isMac ? event.metaKey : event.ctrlKey;

      // Cmd/Ctrl + Z: Undo
      if (isCtrlOrCmd && event.key === 'z' && !event.shiftKey) {
        event.preventDefault();
        undoClicked();
      }
      // Cmd/Ctrl + Shift + Z: Redo
      else if (isCtrlOrCmd && event.key === 'z' && event.shiftKey) {
        event.preventDefault();
        redoClicked();
      }
      // Cmd/Ctrl + '+': Zoom in
      else if (isCtrlOrCmd && (event.key === '+' || event.key === '=')) {
        event.preventDefault();
        if (window.map) {
          window.map.zoomIn();
        }
      }
      // Cmd/Ctrl + '-': Zoom out
      else if (isCtrlOrCmd && event.key === '-') {
        event.preventDefault();
        if (window.map) {
          window.map.zoomOut();
        }
      }
      // Cmd/Ctrl + N: New route
      else if (isCtrlOrCmd && event.key === 'n') {
        event.preventDefault();
        store.dispatch('planTrail/addNewRoute');
      }
      // Cmd/Ctrl + I: Import
      else if (isCtrlOrCmd && event.key === 'i') {
        event.preventDefault();
        handleRouteImport();
      }
      // Cmd/Ctrl + '[': Next routing preference
      else if (isCtrlOrCmd && event.key === '[') {
        event.preventDefault();
        const preferences = ['hike', 'car', 'bike'];
        const currentIndex = preferences.indexOf(routePreference.value);
        const nextIndex = (currentIndex + 1) % preferences.length;
        store.commit('planTrail/setRoutePreference', preferences[nextIndex]);
      }
      // Cmd/Ctrl + ']': Previous routing preference
      else if (isCtrlOrCmd && event.key === ']') {
        event.preventDefault();
        const preferences = ['hike', 'car', 'bike'];
        const currentIndex = preferences.indexOf(routePreference.value);
        const prevIndex = (currentIndex - 1 + preferences.length) % preferences.length;
        store.commit('planTrail/setRoutePreference', preferences[prevIndex]);
      }
    };

    // Create option items with their actions
    const createOptionItems = (): OptionItem[] => {
      return [
        {
          ...optionConfig[OptionType.WAYPOINTS],
          action: () => {
            const optionsModal = Modal.getInstance(document.getElementById('options-dialog'));
            optionsModal?.hide();

            if (window.user) {
              const element = document.getElementById("waypoint-options-dialog");
              const modal = new Modal(element);
              modal.show();
            } else {
              window.onBoardingVue?.launchFor(OnBoardingGoal.Waypoints);
            }
          }
        },
        {
          ...optionConfig[OptionType.GO_TO_LOCATION],
          action: () => {
            const optionsModal = Modal.getInstance(document.getElementById('options-dialog'));
            optionsModal?.hide();

            const element = document.getElementById("go-to-location-dialog");
            const modal = new Modal(element);
            modal.show();
          }
        },
        {
          ...optionConfig[OptionType.CREATE_PRINT_MAP],
          action: async () => {
            const isProOrProPlus = await RevenueCatWrapper.isProOrProPlus();
            if (isProOrProPlus) {
              window.location.href = "/print-maps";
            } else {
              window.location.href = "/purchases";
            }
          }
        }
      ];
    };

    // --- Mobile gate ---
    // The planner is a desktop-only, mouse-oriented tool. On mobile web we don't load it at all —
    // we show the "Open in app" prompt instead. This is reactive so the overlay paints on the FIRST
    // render (the old check ran at the very end of the async onMounted, so the planner loaded fully
    // before the prompt appeared), and it's a real gate: onMounted returns early so no map/store init
    // runs and none of the Teleport/position:fixed panels — which previously escaped the overlay's
    // stacking context — are ever created. Gating rules live in plannerMobileGate.ts (unit-tested).
    const isMobile = ref(isPlannerMobile());
    let plannerInitSkipped = false;
    const handleMobileResize = () => {
      isMobile.value = nextPlannerGateOnResize(plannerInitSkipped, isMobile.value);
    };

    // --- Lifecycle Hooks ---
    onMounted(async () => {
      // Desktop-only: block the planner on mobile web and show the app prompt (see mobile gate above).
      if (isMobile.value) {
        plannerInitSkipped = true;
        return;
      }
      window.addEventListener('resize', handleMobileResize);

      window.openHighlightEditDialog = openHighlightEditDialog;

      // Resolved once the store modules have loaded (routes from edits,
      // ?from_hike_id= copies or restored drafts included). The map's 'load'
      // event awaits this so mapInitialised never runs against empty state.
      let resolveStoreReady = () => {};
      const storeReadyPromise = new Promise((resolve) => { resolveStoreReady = () => resolve(undefined); });

      // --- Map Initialization (Moved from main Vue instance) ---
      mapboxgl.accessToken = "pk.eyJ1IjoiaGlpa2VyLW1hcHMiLCJhIjoiY2x0YThyZHppMWhmejJqbzExODBuY3dveiJ9.nBrtnXgbzxpAx_953HCB-w"; // Use env var ideally

      // Ensure the DOM element exists before creating the map
      const mapContainer = document.getElementById('map');
      if (!mapContainer) {
        console.error("Map container element ('#map') not found.");
        return;
      }

      try {
        map = new mapboxgl.Map(await store.getters['planTrail/initialMapSettings']);

        // Assign to window AFTER creation (if needed globally)
        window.map = map;
        mapInstance.value = map;
        currentLatitude.value = map.getCenter().lat;

        window.layerRegistry = new LayerRegistry(map);

        // Ensure overlay listeners are registered early so persisted overlays restore on refresh
        store.dispatch('mapDataOverlays/initOverlayListeners');

        const canvas = map.getCanvasContainer();
        canvas.style.cursor = "crosshair"; // Initial cursor

        // --- Attach Map Event Listeners ---
        // Use the function references defined in setup directly
        map.on('click', mapClicked);
        map.on('contextmenu', mapRightClicked);
        map.on('moveend', mapMoved);
        // Removed global mousemove - now using layer-specific events in setupOptimizedHover

        map.on('load', async () => {
          emitter.emit('map-loaded'); // Emit on event bus if still used
          createGeoLocate(); // Assumes this function sets up geolocation and adds it to window.map or manages it internally
          // The map can finish loading before the store has loaded routes
          // (edit / from_hike_id copy / draft restore) — wait so
          // mapInitialised sees them and can fit the camera to the hike
          await storeReadyPromise;
          await store.dispatch('planTrail/mapInitialised');
          store.dispatch('planTrail/setMapLatLonFromParams'); // Make sure store is ready for this
          if (window.tracerRouteGpx) {
            await store.dispatch('planTrail/addTracerRoute', window.tracerRouteGpx);
          }
          // Load trail data from URL param if present (for trails@hiiker.co)
          await store.dispatch('planTrail/loadTrailDataFromParam');
          // Setup POI interactions on initial load
          setTimeout(() => {
            store.dispatch('planTrail/setupPOIInteractions');
          }, 1500);
        });

        map.on('style.load', () => {
          ThreeDimensionalMapManager.disable3DMode(); // Ensure this is correctly scoped or static
          // Call the handler function directly
          mapStyleLoaded();
        });


      } catch (error) {
        console.error("Failed to initialize Mapbox map:", error);
      }
      // Initialize option items
      optionItems.value = createOptionItems();

      // Initialize store modules first
      // Use await here to ensure they are ready before map initialization depends on them
      await store.dispatch('planTrail/initialiseStore');
      await store.dispatch('mapStyles/initialiseStore');
      await store.dispatch('waypoints/initialiseStore'); // Moved from main Vue instance
      resolveStoreReady();

      // Migrate to multi-route structure if needed
      store.commit('planTrail/migrateToMultiRoute');

      // Initialize multi-route display
      setTimeout(async () => {
        await store.dispatch('planTrail/addAllRoutesToMap');
        await store.dispatch('planTrail/addHighlightsToMap');
      }, 1500);


      // Initialize Bootstrap Tooltips (if still using Bootstrap JS)
      const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]')
      const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new Tooltip(tooltipTriggerEl))

      emitter.on('map-style-loaded', mapStyleLoaded);
      emitter.on('update-geojson', handleGeoJsonUpdate); // Use a specific handler
      emitter.on('merge-day-names', handleMergeDayNames);
      emitter.on('route-style-settings:changed', handleRouteStyleChanged);

      // Add keyboard shortcuts listener
      window.addEventListener('keydown', handleKeyboardShortcuts);

    });

    onBeforeUnmount(() => {
      // Remove keyboard shortcuts listener
      window.removeEventListener('keydown', handleKeyboardShortcuts);
      window.removeEventListener('resize', handleMobileResize);
      // Clean up map listeners
      if (map) {
        map.off('click', mapClicked);
        map.off('moveend', mapMoved);
        map.off('load'); // Remove anonymous function or specific named function if used
        map.off('style.load'); // Remove anonymous function or specific named function if used
        // Optional: Fully remove map instance
        // map.remove();
        // map = null;
        // window.map = undefined; // Clear global reference if set
      }

      // Clean up event bus listeners
      window.bus?.$off('map-style-loaded', mapStyleLoaded);
      window.bus?.$off('update-geojson', handleGeoJsonUpdate);
      emitter.off('merge-day-names', handleMergeDayNames);
      emitter.off('route-style-settings:changed', handleRouteStyleChanged);

      // Clean up Bootstrap tooltips if necessary
      $('[data-bs-toggle="tooltip"]').tooltip('dispose');

      delete window.openHighlightEditDialog;
      resetPOIModalState();
      cleanupOrphanedBackdrops();
    });

    // Route management methods
    const handleRouteImport = () => {
      // Use existing import functionality
      const element = document.getElementById("trail-import-dialog");
      const modal = new Modal(element);
      modal.show();
    };

    // Drag-and-drop import: dropping a GPX/KML/etc. file anywhere on the planner
    // runs the same import as the "Import Hike" dialog's file picker.
    const isDraggingFile = ref(false);

    const handleFileDragEnter = (e: DragEvent) => {
      if (isFileDrag(e.dataTransfer)) {
        isDraggingFile.value = true;
      }
    };

    const handleFileDragLeave = () => {
      isDraggingFile.value = false;
    };

    const handleFileDrop = async (e: DragEvent) => {
      isDraggingFile.value = false;
      const file = getSupportedImportFile(e.dataTransfer?.files);
      if (!file) {
        alert('That file type is not supported. Drop a GPX, KML, KMZ, TCX or GeoJSON file.');
        return;
      }
      await store.dispatch('planTrail/importFile', file);
    };

    const handleAddNewRoute = () => {
      // Store already handles this via the mutation
    };

    const handleEditRoute = (route: any) => {
      // For now, just update the route name
      const newName = prompt('Enter new route name:', route.name);
      if (newName && newName.trim()) {
        store.commit('planTrail/updateRouteName', {
          routeId: route.id,
          name: newName.trim()
        });
      }
    };

    const handleDeleteRoute = async (route: any) => {
      const shouldDelete = confirm(`Are you sure you want to delete "${route.name}"?`);
      if (!shouldDelete) {
        return;
      }

      store.commit('planTrail/removeRoute', route.id);
      await store.dispatch('planTrail/addAllRoutesToMap');
    };

    const handleReplaceRoute = (route: any) => {
      // Create a file input element
      const input = document.getElementById('file-selector');
      if (!input) {
        return;
      }

      input.type = 'file';
      input.accept = '.gpx,.kml,.kmz,.tcx,.geojson,application/gpx+xml,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz,application/json';
      input.value = '';

      input.onchange = async (e: Event) => {
        const target = e.target as HTMLInputElement;
        if (!target.files || target.files.length === 0) {
          return;
        }

        const file = target.files[0];

        try {
          await store.dispatch('planTrail/replaceRouteFile', { file, routeId: route.id });
        } finally {
          target.value = '';
        }
      };

      input.click();
    };

    const handleCollapseRoutes = () => {
      // Toggle routes panel visibility (placeholder)
    };

    const handleRouteStyleChange = (payload?: { style?: string }) => {
      if (payload?.style === 'transport' && routePreference.value !== 'car') {
        store.commit('planTrail/setRoutePreference', 'car');
      }
    };

    const handleSaveAction = () => {
    };

    const handleShareAction = () => {
    };

    const handleImportAction = () => {
    };

    const handleCollapseActions = () => {
    };

    // Track if user has confirmed editing a referenced route
    const hasConfirmedReferenceEdit = ref(false);

    // Check synchronously if editing is allowed (doesn't show modal).
    // Defaults to the active route; pass a route to check a specific one
    // (e.g. stage connectors, which span two routes).
    const canEditReference = (route = null): boolean => {
      const target = route ?? store.getters['planTrail/activeRoute'];

      // If no route or not a reference, allow edit
      if (!target || !target.isReference) {
        return true;
      }

      // Only allow if already confirmed
      return hasConfirmedReferenceEdit.value;
    };

    // Check if the route (default: active route) is a reference and show
    // the warning modal if needed
    const checkReferenceEditWarning = async (route = null): Promise<boolean> => {
      const target = route ?? store.getters['planTrail/activeRoute'];

      // If no route or not a reference, allow edit
      if (!target || !target.isReference) {
        return true;
      }

      // If already confirmed this session, allow edit
      if (hasConfirmedReferenceEdit.value) {
        return true;
      }

      // Show warning modal and wait for user decision
      if (referenceWarningModal.value) {
        const confirmed = await referenceWarningModal.value.show(target);
        if (confirmed) {
          hasConfirmedReferenceEdit.value = true;
        }
        return confirmed;
      }

      return true;
    };

    // Expose globally for map interactions to check before edits
    window.checkReferenceEditWarning = checkReferenceEditWarning;
    window.canEditReference = canEditReference;

    return {
      // Refs
      isMobile,
      buttons,
      optionItems,
      topProgress, // For template ref
      mapElementRef, // For template ref (optional)
      referenceWarningModal, // For template ref
      routeLengthWarningModal, // For template ref
      mapInstance,
      currentLatitude,
      showMultiDayTutorial,
      multiDayTutorialSteps,

      // Computed Properties
      title,
      routePreference,
      waypointAddingModeEnabled,
      selectedPOI,
      miniLoader,
      isSnap,
      removalMode,
      isLoading,
      trailDistanceString,
      elevationGainString,
      miniCoordsBar,
      activeRouteBounds,

      // Methods
      onMultiDayTutorialDone,
      saveClicked,
      shareClicked,
      buttonClicked,
      undoClicked,
      redoClicked,
      snapClicked,
      outAndBackClicked,
      save, // Make save available if called from modal template directly
      handleAddCustomPOI,
      poiCoordinates,
      editingHighlight,
      closeToastClicked,
      loopRoute,
      reverseRoute,
      backtrackRoute,
      addTracerRoute,
      updateElevation,
      clearMap,
      _3dClicked,

      // Drag-and-drop import
      isDraggingFile,
      handleFileDragEnter,
      handleFileDragLeave,
      handleFileDrop,

      // Route management methods
      handleRouteImport,
      handleAddNewRoute,
      handleEditRoute,
      handleDeleteRoute,
      handleReplaceRoute,
      handleCollapseRoutes,
      handleRouteStyleChange,
      handleSaveAction,
      handleShareAction,
      handleImportAction,
      handleCollapseActions,
      // Map interaction methods are used internally by listeners, no need to return unless called from template
      // mapClicked, mapMoved, mapStyleLoaded (not called directly from template)
    };
  }
});
</script>

<style scoped>

.my-silly-class {
  display: none;
}

.file-drop-overlay {
  position: absolute;
  inset: 0;
  z-index: 2000; /* above map panels and Bootstrap modals (~1055) so drops land here */
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(32, 32, 32, 0.75);
}

.file-drop-overlay-content {
  pointer-events: none; /* stop children firing dragleave on the overlay */
  text-align: center;
  color: #fff;
  border: 2px dashed #fff;
  border-radius: 12px;
  padding: 2.5rem 3.5rem;
}

.file-drop-overlay-content .material-icons {
  font-size: 48px;
}
.map-container {
  flex: 1 1 auto;
  max-width: 100%;
  z-index: 7;
}

.map-content {
  width: 100%;
  height: 100%;
  position: relative;
}

.mapbox {
  top: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
}


.mapboxgl-ctrl-scale {
  background-color: hsla(0, 0%, 100%, .75);
  border: 2px solid #333;
  border-top: #333;
  box-sizing: border-box;
  color: #333;
  font-size: 10px;
  padding: 0 5px;
  white-space: nowrap;
  border-radius: 0 0 10px 10px;
  position: absolute;
  top: 59px;
  left: 5px;
}

.threeDmapImg {
  -webkit-transition: all .3s ease-in-out;
  -moz-transition: all .3s ease-in-out;
  -o-transition: all .3s ease-in-out;
  transition: all .3s ease-in-out;
}

.threeDmapImg:hover {
  bottom: 0;
  filter: hue-rotate(193deg);
  cursor: pointer;
}

.mobileCTA {
  /* Fixed + very high z-index so it covers the whole viewport and sits above every planner panel,
     including the Teleport-to-body modal and position:fixed dropdowns (z-index up to 1000) that used
     to escape the old absolute/z-index:8 overlay. Rendered via v-if, so no visibility toggle needed. */
  position: fixed;
  z-index: 100000;
  inset: 0;
  background: #202020fa;
}


</style>
